/**
 * Bag barcode generator for the dev simulator (hotkey "U" in simulatorTools),
 * plus a deliberately invalid code for hotkey "X".
 *
 * A simulated scan goes through the same validation as a real one, so the
 * generated code has to satisfy the current sample's deal restriction or the
 * scan is rejected. Candidates cover the formats in the CRM "Barcode Types"
 * table; unknown restrictions fall back to the Rogo 12-digit format, which the
 * default rules accept.
 */

const HEX = '0123456789abcdef';

const randomDigits = (count: number) => Array.from({ length: count }, () => Math.floor(Math.random() * 10)).join('');

const randomHex = (count: number) =>
  Array.from({ length: count }, () => HEX[Math.floor(Math.random() * HEX.length)]).join('');

/** Rogo bag format: RG + 12 zero-padded digits (e.g. RG201000033898). */
export const rogoBagBarcode = () => `RG${randomDigits(12)}`;

/**
 * Deliberately invalid bag for the "X" hotkey: letters, a dash and the wrong
 * length, so it fails the default rules and every restriction in CRM.
 */
export const INVALID_SIMULATOR_BAG_BARCODE = 'INVALID-BAG-CODE';

const CANDIDATE_FORMATS: (() => string)[] = [
  rogoBagBarcode, // ROGO / default rules
  () => randomDigits(12), // 12 digit incremented
  () => randomDigits(8), // Agvise
  () => randomDigits(7), // Agvise short / Suretech short
  () => randomDigits(5), // ALS
  () => `${randomDigits(4)}-${randomDigits(6)}`, // Suretech dashed
  () => randomHex(64), // Simplot Grandview
];

export function makeSimulatorBagBarcode(barcodeRegex?: string | null): string {
  if (!barcodeRegex) {
    return rogoBagBarcode();
  }

  let regex: RegExp;

  try {
    regex = new RegExp(barcodeRegex);
  } catch {
    // same fallback as isBarcodeValidBag: a broken restriction means default rules
    return rogoBagBarcode();
  }

  const candidate = CANDIDATE_FORMATS.map((makeCandidate) => makeCandidate()).find((code) => regex.test(code));

  return candidate ?? rogoBagBarcode();
}
